/**
 * La vue d'aigle du mode `?demo` (ARTO-58). Les chiffres sont ceux, fictifs,
 * des maquettes (`docs/maquettes/2026-10-01-vue-d-aigle/`) : ils n'existent
 * que dans les fichiers de démonstration `lib/demo*.ts`, nulle part ailleurs —
 * aucun montant, tranche ni pourcentage n'entre dans le code de production,
 * où un montant non saisi vaut `null` (« à saisir »).
 *
 * La projection suit celle de l'API (`apps/api/src/console/pilotage/
 * projection.ts`, `CONTRAT-API.md`) : un associé reçoit la vue `associe`
 * quoi qu'il demande ; un rôle opérationnel qui demande `associe` la reçoit
 * aussi, avec un partage vide ; sinon la vue `admin`, dont le partage ne
 * s'ouvre qu'à qui peut gérer les associés. Dans la vue `associe`,
 * `parc.detail`, `aTraiter` et `abonnements` sont ABSENTS, et aucun nom de
 * galerie n'apparaît nulle part.
 */
import { can, sujet } from '@artosera/shared/permissions';
import type { NomVueDAigle, PeriodeVueDAigle, RoleConsole, VueDAigle } from '../types.js';

/** Le mois courant de la démonstration : celui des maquettes. */
const MOIS_COURANT = '2026-09';

/** Les mois affichés : le mois courant et 1, 3 ou 12 mois avant lui. */
const MOIS_PAR_PERIODE: Record<PeriodeVueDAigle, number> = { '30j': 1, '90j': 3, '12m': 12 };

function moisAvant(mois: string, retrait: number): string {
  const [annee, numero] = mois.split('-').map(Number) as [number, number];
  const rang = annee * 12 + (numero - 1) - retrait;
  return `${Math.floor(rang / 12)}-${String((rang % 12) + 1).padStart(2, '0')}`;
}

/** `[mois, …]` du plus ancien au mois courant, pour la période demandée. */
function moisDeLaPeriode(periode: PeriodeVueDAigle): string[] {
  const retraits = MOIS_PAR_PERIODE[periode];
  return Array.from({ length: retraits + 1 }, (_, i) => moisAvant(MOIS_COURANT, retraits - i));
}

/** Revenu mensuel récurrent (euros) sur 13 mois, de septembre 2025 à septembre 2026. */
const MRR_EUROS = [
  2600, 2900, 3300, 3700, 4000, 4350, 4700, 5070, 5670, 6280, 6900, 7530, 8160,
] as const;
const MOIS_MRR = moisDeLaPeriode('12m');

function mrrDuMois(mois: string): number {
  return (MRR_EUROS[MOIS_MRR.indexOf(mois)] ?? 0) * 100;
}

/** Parc fin de chaque mois : [production, essai, résiliées cumulées]. */
const PARC_PAR_MOIS: ReadonlyArray<readonly [number, number, number]> = [
  [6, 2, 0],
  [7, 3, 0],
  [8, 3, 0],
  [8, 4, 0],
  [9, 3, 0],
  [10, 3, 0],
  [10, 4, 1],
  [11, 4, 1],
  [12, 3, 1],
  [12, 4, 1],
  [13, 4, 1],
  [13, 4, 1],
  [14, 4, 2],
];

/** Les galeries du parc, fictives (maquette `plateforme.html`). */
const GALERIES_DEMO: NonNullable<VueDAigle['parc']['detail']> = [
  ['Galerie Verdelet', 'production', 'galerie', 2, '2026-09-09', null],
  ['Atelier Tramontane', 'production', 'essentiel', 2, '2026-08-24', null],
  ['Galerie Pierre-Feu', 'production', 'galerie', 4, '2026-07-15', null],
  ['Maison Piment', 'production', 'galerie', 3, '2026-06-16', null],
  ['Galerie des Trois Fontaines', 'production', 'galerie', 5, '2026-05-12', null],
  ['Atelier Garance', 'production', 'essentiel', 2, '2026-04-27', null],
  ['Galerie Cobalt', 'production', 'galerie', 5, '2026-04-02', null],
  ['Maison Fenouil', 'production', 'essentiel', 3, '2026-03-10', null],
  ['Galerie Quai des Ardoises', 'production', 'groupe', 7, '2026-02-03', null],
  ['Atelier Sénevé', 'production', 'galerie', 5, '2026-01-19', null],
  ['Galerie Brise-Marée', 'production', 'essentiel', 2, '2025-12-08', null],
  ['Maison Ocre', 'production', 'galerie', 6, '2025-11-17', null],
  ['Atelier Lisière', 'production', 'galerie', 6, '2025-09-22', null],
  ['Galerie Vermeil', 'production', 'groupe', 8, '2025-06-12', null],
  ['Galerie Mirabelle', 'essai', 'galerie', 1, null, '2026-10-06'],
  ['Atelier Orpiment', 'essai', 'essentiel', 0, null, '2026-10-21'],
  ['Maison Sablonne', 'essai', 'galerie', 2, null, '2026-10-28'],
  ['Galerie Lanterne', 'essai', 'essentiel', 0, null, '2026-11-09'],
  ['Galerie Bleu-Outremer', 'resiliee', 'essentiel', 0, null, null],
  ['Atelier Calcaire', 'resiliee', 'essentiel', 0, null, null],
].map(([nom, etat, palier, modules, entree, finEssai], rang) => ({
  id: `demo-parc-${rang + 1}`,
  nom: nom as string,
  etat: etat as 'production' | 'essai' | 'resiliee',
  palier: palier as string,
  modules: modules as number,
  entreeLe: entree === null ? null : `${entree as string}T00:00:00.000Z`,
  finEssaiLe: finEssai === null ? null : `${finEssai as string}T00:00:00.000Z`,
}));

const ABONNEMENTS_DEMO: NonNullable<VueDAigle['abonnements']> = [
  ['Maison Ocre', 'galerie', 39000, 1000, '2026-11-17'],
  ['Galerie Brise-Marée', 'essentiel', 24000, 0, '2026-12-08'],
  ['Atelier Sénevé', 'galerie', 41000, 0, '2027-01-19'],
  ['Galerie Quai des Ardoises', 'groupe', 56000, 1500, '2027-02-03'],
  ['Maison Fenouil', 'essentiel', 26000, 0, '2027-03-10'],
  ['Galerie Cobalt', 'galerie', 38000, 1000, '2027-04-02'],
].map(([galerie, palier, montant, remise, echeance], rang) => ({
  id: `demo-abo-${rang + 1}`,
  galerie: galerie as string,
  palier: palier as string,
  nature: 'galerie',
  montantMensuelCentimes: montant as number,
  remisePointsDeBase: remise as number,
  etat: 'actif',
  echeanceEngagementLe: `${echeance as string}T00:00:00.000Z`,
}));

const A_TRAITER_DEMO: NonNullable<VueDAigle['aTraiter']> = [
  {
    type: 'creance_en_retard',
    libelle: 'Une somme due est en retard de paiement.',
    lien: '/finances?onglet=creances',
  },
  {
    type: 'essai_echu',
    libelle: 'Galerie Mirabelle : fin d’essai le 6 octobre, aucun abonnement choisi.',
    lien: '/galeries',
  },
  {
    type: 'montant_a_saisir',
    libelle: 'Un revenu ponctuel attend son montant.',
    lien: '/finances?onglet=revenus',
  },
  {
    type: 'couts_a_saisir',
    libelle: 'Les coûts d’octobre ne sont pas encore saisis.',
    lien: '/finances?onglet=couts',
  },
];

/** Le partage entre associés : lignes fictives, versements calculés sur le revenu du mois. */
const PARTAGE_DEMO = [
  { libelle: 'Solène Varenne', partPointsDeBase: 2000 },
  { libelle: 'Armand Quérel', partPointsDeBase: 1500 },
  { libelle: 'Inès Delarocque', partPointsDeBase: 1000 },
] as const;

function partageDemo(periode: PeriodeVueDAigle, lignes: number[]): VueDAigle['partage'] {
  const mois = moisDeLaPeriode(periode);
  return lignes.map((indice) => {
    const ligne = PARTAGE_DEMO[indice]!;
    return {
      libelle: ligne.libelle,
      partPointsDeBase: ligne.partPointsDeBase,
      versements: mois.map((m) => {
        // Une base de partage de 10 % du revenu récurrent, pour la démonstration.
        const du = Math.round((mrrDuMois(m) * 0.1 * ligne.partPointsDeBase) / 10_000);
        const versement = m === MOIS_COURANT ? 0 : du;
        // Un mois sans montant saisi, pour montrer l'état « à saisir ».
        const aSaisir = indice === 2 && m === '2026-07';
        return {
          mois: m,
          montantDuCentimes: aSaisir ? null : du,
          verseCentimes: aSaisir ? 0 : versement,
        };
      }),
    };
  });
}

/**
 * Le compte de démonstration d'un rôle (`?demo&role=associe|support|
 * exploitation|direction`) : la direction par défaut.
 */
export function vueDAigleDemo(
  role: RoleConsole,
  periode: PeriodeVueDAigle,
  demandee?: NomVueDAigle,
): VueDAigle {
  // La matrice décide, pas une comparaison de rôle : qui ne peut pas lire la
  // console (`console:read`) n'a que la vue d'aigle, donc la vue `associe`.
  const operationnel = can(sujet(role), 'console:read');
  const associe = !operationnel || demandee === 'associe';
  const mois = moisDeLaPeriode(periode);
  const nombreMois = mois.length;
  const parc = PARC_PAR_MOIS.slice(-nombreMois);

  let partage: VueDAigle['partage'] = [];
  if (!operationnel) partage = partageDemo(periode, [0]);
  else if (!associe && can(sujet(role), 'console:manage-associates')) {
    partage = partageDemo(periode, [0, 1, 2]);
  }

  const vue: VueDAigle = {
    vue: associe ? 'associe' : 'admin',
    genereeLe: '2026-09-30T22:30:00.000Z',
    periode,
    indicateurs: {
      mrrCentimes: 816_000,
      arrCentimes: 9_792_000,
      abonnementsSansMontant: 0,
      galeriesEnProduction: 14,
      galeriesEnEssai: 4,
      margeMoisCentimes: 638_000,
      utilisateursActifs30j: 64,
    },
    revenus: {
      mois: MOIS_COURANT,
      abonnementsParPalier: [
        { palier: 'essentiel', nombre: 4, montantCentimes: 102_000, aSaisir: 0 },
        { palier: 'galerie', nombre: 8, montantCentimes: 334_000, aSaisir: 0 },
        { palier: 'groupe', nombre: 2, montantCentimes: 150_000, aSaisir: 0 },
      ],
      forfaitsParTranche: [
        { libelle: '1 à 5 galeries', exploitants: 1, montantCentimes: 90_000, aSaisir: 0 },
        { libelle: '6 à 10 galeries', exploitants: 1, montantCentimes: 140_000, aSaisir: 0 },
        { libelle: '11 à 20 galeries', exploitants: 0, montantCentimes: 0, aSaisir: 0 },
        { libelle: '21 à 35 galeries', exploitants: 0, montantCentimes: 0, aSaisir: 0 },
      ],
      forfaitsHorsGrille: { exploitants: 0, montantCentimes: 0, aSaisir: 0 },
      ponctuelsParCategorie: [
        { categorie: 'Formation', montantCentimes: 28_000, aSaisir: 0 },
        { categorie: 'Mise en service', montantCentimes: 46_000, aSaisir: 0 },
        { categorie: 'Développement sur mesure', montantCentimes: 12_000, aSaisir: 1 },
      ],
      totalMoisCentimes: 902_000,
      historiqueMrr: mois.map((m) => ({ mois: m, mrrCentimes: mrrDuMois(m) })),
    },
    sommesDues: {
      dueCentimes: 342_000,
      enRetardCentimes: 96_000,
      encaisseMoisCentimes: 410_000,
      aSaisir: 1,
    },
    couts: {
      mois: MOIS_COURANT,
      parPoste: [
        { poste: 'Hébergement', montantCentimes: 82_000 },
        { poste: 'Stockage objet', montantCentimes: 34_000 },
        { poste: 'E-mails transactionnels', montantCentimes: 19_000 },
        { poste: 'Supervision', montantCentimes: 26_000 },
        { poste: 'Divers', montantCentimes: 17_000 },
      ],
      totalCentimes: 178_000,
    },
    parc: {
      parEtat: { production: 14, essai: 4, sansContrat: 0, suspendues: 0, resiliees: 2 },
      parPalier: [
        { palier: 'essentiel', nombre: 4 },
        { palier: 'galerie', nombre: 8 },
        { palier: 'groupe', nombre: 2 },
      ],
      parTrancheDeModules: [
        { tranche: '0-2', nombre: 4 },
        { tranche: '3-5', nombre: 6 },
        { tranche: '6+', nombre: 4 },
      ],
      historique: mois.map((m, i) => ({
        mois: m,
        production: parc[i]![0],
        essai: parc[i]![1],
        sansContrat: 0,
        resiliees: parc[i]![2],
      })),
      ...(associe ? {} : { detail: GALERIES_DEMO.map((galerie) => ({ ...galerie })) }),
    },
    modules: [
      { module: 'facturation', galeries: 11, sur: 14 },
      { module: 'coffre', galeries: 9, sur: 14 },
      { module: 'editions-provenance', galeries: 8, sur: 14 },
      { module: 'roles-fins', galeries: 7, sur: 14 },
      { module: 'documents-prestige', galeries: 7, sur: 14 },
      { module: 'boutique', galeries: 5, sur: 14 },
      { module: 'newsletter-reseaux', galeries: 4, sur: 14 },
      { module: 'viewing-rooms', galeries: 4, sur: 14 },
      { module: 'mode-foire', galeries: 3, sur: 14 },
      { module: 'artsy-artnet', galeries: 2, sur: 14 },
    ],
    usage: {
      utilisateursActifs30j: 64,
      oeuvres: 18_240,
      stockageOctets: 412 * 1024 ** 3,
      mesureLe: '2026-10-01T04:00:00.000Z',
    },
    sante: {
      statut: 'a_surveiller',
      points: [
        { cle: 'base', statut: 'ok', depuis: null },
        { cle: 'stockage', statut: 'ok', depuis: null },
        { cle: 'travaux', statut: 'ok', depuis: null },
        { cle: 'sauvegardes', statut: 'a_surveiller', depuis: '2026-09-30T03:12:00.000Z' },
      ],
    },
    jalons: [
      { libelle: 'Maquette Halo validée', echeanceLe: '2026-09-17T00:00:00.000Z', etat: 'atteint' },
      { libelle: 'Démo décideur', echeanceLe: '2026-11-09T00:00:00.000Z', etat: 'en_cours' },
      { libelle: 'Bascule en production', echeanceLe: '2026-12-15T00:00:00.000Z', etat: 'a_venir' },
      { libelle: 'Réseau de galeries', echeanceLe: '2027-01-15T00:00:00.000Z', etat: 'a_venir' },
    ],
    partage,
  };

  if (!associe) {
    vue.aTraiter = A_TRAITER_DEMO.map((ligne) => ({ ...ligne }));
    vue.abonnements = ABONNEMENTS_DEMO.map((ligne) => ({ ...ligne }));
  }
  return vue;
}
